import {useMemo, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {useHeaderContext} from '../../../contexts/header-context'
import {useGlobalHeader} from '../../../contexts/global-elements-context'
import {useNavigationContext} from '../../../contexts/navigation-context'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    HEADER_GRADIENT_MAP,
    HEADER_COMPOSITING_LAYER,
    HEADER_ICON_FILTER,
    HEADER_DROP_SHADOW,
    HEADER_VARIATION_CMS_MAP,
    HEADER_GRADIENT_CMS_MAP
} from '../config'

/**
 * Hook that encapsulates all transparent header variant logic:
 * hover detection, visual state computation, gradient, drop shadow,
 * logo/icon color filters.
 *
 * Transparent header rules:
 *  - Homepage: context defaults apply unless a CMS HeaderVariations entry matches.
 *  - Other URLs: only an exact CMS UrlToApply match can enable the transparent
 *    variant; otherwise the header stays opaque (variant '3').
 */
const useTransparentHeader = (styles) => {
    const {
        headerRef,
        headerVariant,
        headerGradient,
        headerDropShadow,
        isScrolled,
        headerHoverZone,
        setHeaderHoverZone
    } = useHeaderContext()

    const {data: headerData} = useGlobalHeader()
    const {pathname} = useLocation()
    const {isHomepagePath} = useNavigationContext()

    const isDesktop = useBreakpointValue({base: false, lg: true})

    // Transparent header is reserved for the homepage by default, but CMS
    // HeaderVariations may opt-in additional URLs via an exact UrlToApply match.
    const isHomepage = isHomepagePath(pathname)

    // Resolve CMS variation by exact URL match for any pathname; off-homepage we
    // ignore the wildcard fallback so non-CMS pages stay opaque.
    const cmsVariation = (() => {
        if (!Array.isArray(headerData?.HeaderVariations)) return null
        const specific = headerData.HeaderVariations.find(
            (v) => v.urlToApply && pathname === v.urlToApply
        )
        if (specific) return specific
        // Wildcard (empty UrlToApply) only applies on homepage
        return isHomepage
            ? headerData.HeaderVariations.find((v) => !v.urlToApply) || null
            : null
    })()

    // Effective values: CMS match wins. Otherwise use context defaults on the
    // homepage; force opaque ('3') everywhere else.
    const effectiveVariant = cmsVariation
        ? HEADER_VARIATION_CMS_MAP[cmsVariation.headerVariation] || '3'
        : isHomepage
            ? headerVariant
            : '3'
    const effectiveGradient = cmsVariation
        ? HEADER_GRADIENT_CMS_MAP[cmsVariation.headerGradient] || 'none'
        : isHomepage
            ? headerGradient
            : 'none'
    const effectiveDropShadow = cmsVariation
        ? cmsVariation.headerDropShadow
        : isHomepage
            ? headerDropShadow
            : false

    const isTransparent = effectiveVariant === '1' || effectiveVariant === '2'
    const isWhiteVariant = effectiveVariant === '1'
    const isHeaderHovered = headerHoverZone !== 'none'
    const shouldBeTransparent = isTransparent && !isScrolled && !isHeaderHovered

    // Reliable hover detection via document-level pointermove.
    // onMouseEnter/onMouseLeave misses cases: modal overlays (Portals),
    // navigation re-renders, and overlay DOM removal.
    // Also considers open megamenu popovers (rendered as portals outside header DOM).
    useEffect(() => {
        if (!isTransparent || !isDesktop) return

        let inside = false

        const onPointerMove = (e) => {
            const headerEl = headerRef.current
            if (!headerEl) return

            const inHeader = headerEl.contains(e.target)
            const inPopover = !!e.target.closest?.('[data-testid="popover-menu"]')
            const nowInside = inHeader || inPopover

            if (nowInside !== inside) {
                inside = nowInside
                setHeaderHoverZone(nowInside ? 'header' : 'none')
            }
        }

        document.addEventListener('pointermove', onPointerMove, {passive: true})
        return () => document.removeEventListener('pointermove', onPointerMove)
    }, [isTransparent, isDesktop, setHeaderHoverZone])

    // Container style based on variant + scroll + hover
    const getContainerVariantStyle = () => {
        if (!isTransparent) return {}
        if (isHeaderHovered) {
            return {
                ...styles.containerTransparent,
                ...styles.containerTransparentHovered,
                // Force own compositing layer so the overlay's backdrop-filter
                // cannot bleed through the header background
                ...HEADER_COMPOSITING_LAYER
            }
        }
        if (isScrolled) {
            return {
                ...styles.containerTransparent,
                ...styles.containerTransparentScrolled
            }
        }
        return styles.containerTransparent
    }

    // Text/icon color: white only for V1 in transparent default state
    const textColor = shouldBeTransparent && isWhiteVariant ? 'white' : undefined

    // Logo CSS filter
    const logoFilterStyle = useMemo(() => {
        if (!shouldBeTransparent) return HEADER_ICON_FILTER.default
        if (isWhiteVariant) return HEADER_ICON_FILTER.whiteVariant
        return HEADER_ICON_FILTER.darkVariant
    }, [shouldBeTransparent, isWhiteVariant])

    // CMS icon color filter
    const cmsIconColorFilter = useMemo(() => {
        if (!shouldBeTransparent) return undefined
        if (isWhiteVariant) return HEADER_ICON_FILTER.whiteVariant
        return HEADER_ICON_FILTER.darkVariant
    }, [shouldBeTransparent, isWhiteVariant])

    // Gradient background
    const gradientBackground = useMemo(() => {
        if (!shouldBeTransparent || effectiveGradient === 'none') return null
        return HEADER_GRADIENT_MAP[effectiveGradient] || null
    }, [shouldBeTransparent, effectiveGradient])

    // Drop shadow style for text/logo readability
    const dropShadowStyle = shouldBeTransparent && effectiveDropShadow ? HEADER_DROP_SHADOW : {}

    return {
        isTransparent,
        getContainerVariantStyle,
        textColor,
        logoFilterStyle,
        cmsIconColorFilter,
        gradientBackground,
        dropShadowStyle
    }
}

export default useTransparentHeader
